import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Check } from 'react-feather';
import PageContainer from '../components/PageContainer';
import DirectoryCard from '../components/sso/DirectoryCard';
import SsoSettingsCard from '../components/sso/SsoSettingsCard';
import IdentitiesCard from '../components/sso/IdentitiesCard';
import { API_BASE_URL } from '../config/api';
import '../styles/account.css';
import '../styles/sso.css';

/* /settings/sso, where an admin connects the organisation's directory.
 *
 * This is the destination the backend's link callback redirects to, with
 * ?linked=1 on success, so the banner below is the confirmation for a flow
 * that finished on another origin.
 *
 * `can_manage` is read from the server rather than inferred from the local
 * role, matching TwoFactorSection: the UI then hides a control for exactly the
 * reason the endpoint would refuse it, instead of merely alongside it.
 *
 * Every /api/sso/* route 404s while the backend's SSO_ENABLED is off. That is
 * indistinguishable here from a missing route, so a 404 on load renders the
 * "not available" state rather than an error.
 */

const SsoSettings = () => {
  const [params, setParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState('');
  const [tenant, setTenant] = useState(null);
  const [canManage, setCanManage] = useState(false);
  const [identities, setIdentities] = useState([]);
  const [users, setUsers] = useState([]);
  const [justLinked, setJustLinked] = useState(params.get('linked') === '1');

  const authHeaders = useCallback(
    () => ({
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('access_token')}`
    }),
    []
  );

  // Drop ?linked=1 once read, so a refresh does not replay the confirmation.
  useEffect(() => {
    if (params.get('linked') === '1') {
      const next = new URLSearchParams(params);
      next.delete('linked');
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const load = useCallback(async () => {
    setError('');

    try {
      const res = await fetch(`${API_BASE_URL}/api/sso/tenant`, {
        headers: authHeaders()
      });

      if (res.status === 404) {
        setUnavailable(true);
        return;
      }
      if (!res.ok) throw new Error('Could not load your SSO settings.');

      const data = await res.json();
      setCanManage(Boolean(data.can_manage));
      setTenant(data.linked ? data.tenant : null);

      // The identities endpoint is admin-only and pointless with no directory,
      // so it is fetched only when both hold. A 403 here is not an error worth
      // showing, the card simply does not render for a non-admin.
      if (data.linked && data.can_manage) {
        const idRes = await fetch(`${API_BASE_URL}/api/sso/identities`, {
          headers: authHeaders()
        });
        if (idRes.ok) {
          const idData = await idRes.json();
          setIdentities(idData.identities || []);
          setUsers(idData.users || []);
        }
      } else {
        setIdentities([]);
        setUsers([]);
      }
    } catch (err) {
      setError(err.message || 'Could not load your SSO settings.');
    }
  }, [authHeaders]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      await load();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  const pendingCount = identities.filter((i) => i.status === 'PENDING').length;

  return (
    <PageContainer
      loading={loading}
      loadingMessage="Loading single sign-on settings..."
      title="Single sign-on"
      subtitle="Connect Microsoft Entra ID so your team signs in with their work account"
    >
      <div className="account-page">
        {unavailable ? (
          <div className="account-banner error" role="alert">
            <AlertCircle aria-hidden="true" />
            <span>
              Single sign-on is not switched on for this environment yet. Contact
              support if you expected it to be available.
            </span>
          </div>
        ) : (
          <>
            {justLinked && (
              <div className="account-banner success" role="status">
                <Check aria-hidden="true" />
                <span>
                  Microsoft Entra ID is connected.{' '}
                  {pendingCount > 0
                    ? 'Review the people waiting for approval below.'
                    : 'Your team can now sign in with Microsoft.'}
                </span>
                <button
                  type="button"
                  className="sso-banner-dismiss"
                  onClick={() => setJustLinked(false)}
                  aria-label="Dismiss"
                >
                  ×
                </button>
              </div>
            )}

            {error && (
              <div className="account-banner error" role="alert">
                <AlertCircle aria-hidden="true" />
                <span>{error}</span>
              </div>
            )}

            <div className="account-sections">
              <DirectoryCard
                tenant={tenant}
                canManage={canManage}
                authHeaders={authHeaders}
                onChanged={load}
              />
              {tenant && canManage && (
                <SsoSettingsCard
                  tenant={tenant}
                  canManage={canManage}
                  authHeaders={authHeaders}
                  onChanged={load}
                />
              )}
            </div>

            {tenant && canManage && (
              <div className="sso-identities-block">
                <IdentitiesCard
                  identities={identities}
                  users={users}
                  authHeaders={authHeaders}
                  onChanged={load}
                />
              </div>
            )}
          </>
        )}
      </div>
    </PageContainer>
  );
};

export default SsoSettings;
